/**
 * choir-pitch.js — page-side facade of the choir multi-pitch network (window.ChoirPitch).
 * The network runs in choir-worker.js; the pure recipe lives in choir-shared.js.
 *
 * What it answers: «which pitches does the choir SING in every 11.6 ms frame» — every voice
 * at once, from the whole recording, and an organ or piano under it is not heard as voices.
 * /audio-to-notes deals those frames out into parts (__atnClean.splitVoices). Measured
 * 2026-10-18 on Cantoría (CC BY 4.0, 33 recordings the network never saw): per-part note F
 * 0.739 against 0.298 from Basic Pitch's notes, frame F 0.866 against 0.663.
 *
 * Runs on a VERIFIED GPU only: three minutes of song are ~7 T multiply-adds (6.6 s on an
 * RTX 4060; 85 s on 24 CPU threads — a browser's single WASM thread would take a quarter of
 * an hour). Same rules as rmvpe-pitch.js: native WebGPU build, one provider per worker,
 * a known answer before and after the work, deadlines in visible time.
 *
 * Public API:
 *   support()                       → Promise<{ok, reason?, providers}>   cheap, downloads nothing
 *   track(audioBuffer, {signal, onProgress, slug}) → Promise<{ frames:[[{m, s}]], hop, provider, ms }>
 *   release()                       → terminates the worker
 */
(function () {
'use strict';

var SR = 22050;
var MIN_DEVICE_GB = 4;

function meta(name) { var m = document.querySelector('meta[name="' + name + '"]'); return (m && m.content) || ''; }
function abs(u) { try { return new URL(u, window.location.href).href; } catch (_) { return u; } }
function tele(slug, action, payload) {
    try { if (typeof window.__ae === 'function') window.__ae('feature_use', Object.assign({ slug: slug || 'choir', f: action }, payload || {})); } catch (_) {}
}
function typed(code, message) { var e = new Error(message || code); e.code = code; return e; }
function loadScript(path, probe) {
    if (probe()) return Promise.resolve();
    return new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = window.__jsSrc ? window.__jsSrc(path) : path;
        s.onload = function () { probe() ? resolve() : reject(typed('dep_missing', path + ' loaded without its global')); };
        s.onerror = function () { reject(typed('dep_missing', path + ' failed to load')); };
        document.head.appendChild(s);
    });
}
function needVerifier() { return loadScript('/js/ort-ep-verify.js', function () { return !!window.OrtEpVerify; }); }
function needDownloader() { return loadScript('/js/model-download.js', function () { return !!self.__mdFetchBytes; }); }

var adapterInfo = null, cacheKey = null;

function support() {
    if (!window.Worker) return Promise.resolve({ ok: false, reason: 'no_worker' });
    if (!meta('choir-worker-url') || !meta('choir-model-url') || !window.__ortBuild) return Promise.resolve({ ok: false, reason: 'not_wired' });
    var mem = navigator.deviceMemory;
    if (typeof mem === 'number' && mem < MIN_DEVICE_GB) return Promise.resolve({ ok: false, reason: 'low_memory' });
    var native = typeof window.__ortBuild.nativeGpu === 'function' ? window.__ortBuild.nativeGpu() : null;
    if (!native) return Promise.resolve({ ok: false, reason: 'webkit' });
    if (!navigator.gpu) return Promise.resolve({ ok: false, reason: 'no_webgpu' });
    return needVerifier().then(function () {
        return window.OrtEpVerify.adapterIdentity();
    }).then(function (id) {
        adapterInfo = id || null;
        if (!id || !id.present) return { ok: false, reason: 'no_webgpu' };
        if (id.fallback) return { ok: false, reason: 'software_gpu' };
        var ua = (navigator.userAgent.match(/(?:Chrome|Firefox|Edg)\/(\d+)/) || [])[1] || '0';
        cacheKey = window.OrtEpVerify.verdictKey(['choir', 'ort-native', id.vendor, id.architecture, 'b' + ua]);
        if (window.OrtEpVerify.isRejected(cacheKey, 'webgpu')) return { ok: false, reason: 'gpu_rejected' };
        return { ok: true, providers: ['webgpu'], build: native, adapter: id };
    }, function () { return { ok: false, reason: 'no_webgpu' }; });
}

var worker = null, ready = null, readyInfo = null, pending = {}, seq = 0, currentSlug = null;

function release() {
    if (worker) { try { worker.terminate(); } catch (_) {} }
    worker = null; ready = null; readyInfo = null;
    Object.keys(pending).forEach(function (id) { try { pending[id].reject(typed('released', 'choir network released')); } catch (_) {} });
    pending = {};
}

function onVerdict(v) {
    var flat = window.OrtEpVerify ? window.OrtEpVerify.telemetryOf(v, adapterInfo) : { ep: v && v.provider, ok: v && v.ok ? 1 : 0 };
    flat.why = v && v.why || null; flat.hit = v && v.hit; flat.extra = v && v.extra; flat.chunk_ms = v && v.chunkMs || null;
    tele(currentSlug, 'choir_verdict', flat);
    // Only a WRONG ANSWER is remembered (30 days); a throw or a cancel is a bad day.
    if (v && !v.ok && !v.error && !v.skipped && v.provider === 'webgpu' && cacheKey && window.OrtEpVerify) {
        try { window.OrtEpVerify.remember(cacheKey, 'webgpu', { kind: 'kat', why: v.why || null }); } catch (_) {}
    }
}

var READY_DEADLINE_MS = 120000;
function visibleTimeout(fn, ms) {
    if (typeof window.__visibleTimeout === 'function') return window.__visibleTimeout(fn, ms);
    var t = setTimeout(fn, ms); return function () { clearTimeout(t); };
}

function startWorker(build, bytes, slug) {
    return new Promise(function (resolve, reject) {
        var w, done = false, cancelDeadline = null;
        var settle = function (err, info) {
            if (done) return; done = true;
            if (cancelDeadline) cancelDeadline();
            if (err) { try { w && w.terminate(); } catch (_) {} if (worker === w) worker = null; reject(err); }
            else resolve(info);
        };
        try { w = new Worker(meta('choir-worker-url')); }
        catch (e) { settle(typed('worker_failed', String(e && e.message || e))); return; }
        worker = w;
        w.onerror = function (ev) {
            var err = typed('worker_failed', (ev && ev.message) || 'worker error');
            if (!done) { settle(err); return; }
            Object.keys(pending).forEach(function (id) { pending[id].reject(err); });
            pending = {};
        };
        w.onmessage = function (ev) {
            var m = ev.data || {};
            if (m.type === 'verdict') onVerdict(m.verdict);
            else if (m.type === 'ready') settle(null, { provider: m.provider, chunkMs: m.chunkMs, unverified: !!m.unverified });
            else if (m.type === 'progress') { var p = pending[m.id]; if (p && p.onProgress) p.onProgress(m.done, m.of); }
            else if (m.type === 'result') { var r = pending[m.id]; if (r) { delete pending[m.id]; r.resolve(m); } }
            else if (m.type === 'error') {
                var err = typed(m.code || 'choir_error', m.message);
                err.verdicts = m.verdicts || null;
                if (m.id != null && pending[m.id]) { var q = pending[m.id]; delete pending[m.id]; q.reject(err); }
                else settle(err);
            }
        };
        cancelDeadline = visibleTimeout(function () {
            tele(slug, 'choir_rung_timeout', { ms: READY_DEADLINE_MS });
            settle(typed('rung_timeout', 'webgpu did not become ready'));
        }, READY_DEADLINE_MS);
        var buf = bytes.buffer.byteLength === bytes.length ? bytes.buffer : bytes.slice().buffer;
        w.postMessage({
            type: 'init', modelBytes: buf, providers: ['webgpu'],
            ortUrl: abs(build.url), wasmBase: abs(build.wasmBase),
            ortFallbackUrl: abs(build.fallbackUrl), fallbackWasmBase: abs(build.fallbackWasmBase)
        }, [buf]);
    });
}

function preload(slug, opts) {
    opts = opts || {};
    currentSlug = slug || currentSlug;
    if (ready) return ready;
    var t0 = performance.now();
    ready = support().then(function (sup) {
        if (!sup.ok) throw typed('choir_unsupported_' + sup.reason, 'the choir network cannot run here: ' + sup.reason);
        return needDownloader().then(function () {
            var urls = [meta('choir-model-url'), meta('choir-model-url-fallback')].filter(Boolean);
            var tryUrl = function (i) {
                return self.__mdFetchBytes(urls[i], {
                    signal: opts.signal,
                    onProgress: function (got, total) { if (opts.onProgress && total > 0) opts.onProgress(Math.min(1, got / total)); }
                }).catch(function (e) {
                    if (i + 1 < urls.length && !(opts.signal && opts.signal.aborted)) { tele(slug, 'choir_model_fallback', { reason: String(e && e.message || e).slice(0, 80) }); return tryUrl(i + 1); }
                    throw e;
                });
            };
            return tryUrl(0);
        }).then(function (bytes) { return startWorker(sup.build, bytes, slug); });
    }).then(function (info) {
        readyInfo = info;
        tele(slug, 'choir_preload_ok', { ms: Math.round(performance.now() - t0), ep: info.provider, chunk_ms: info.chunkMs || 0 });
        return info;
    }).catch(function (e) {
        tele(slug, 'choir_preload_fail', { code: e && e.code || null, msg: String(e && e.message || e).slice(0, 80) });
        release();
        throw e;
    });
    return ready;
}

/** Mono 22 050 Hz — the network's passport. OfflineAudioContext downmixes and resamples. */
function to22kMono(audioBuffer) {
    var Ctor = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    var n = Math.max(1, Math.round(audioBuffer.duration * SR));
    var ctx = new Ctor(1, n, SR);
    var node = ctx.createBufferSource();
    node.buffer = audioBuffer; node.connect(ctx.destination); node.start();
    return ctx.startRendering().then(function (b) { return new Float32Array(b.getChannelData(0)); });
}

function abortable(promise, signal) {
    if (!signal) return promise;
    return new Promise(function (resolve, reject) {
        var stop = function () { reject(new DOMException('aborted', 'AbortError')); release(); };
        if (signal.aborted) { stop(); return; }
        signal.addEventListener('abort', stop, { once: true });
        promise.then(function (v) { signal.removeEventListener('abort', stop); resolve(v); },
                     function (e) { signal.removeEventListener('abort', stop); reject(e); });
    });
}

function unpack(m) {
    var frames = new Array(m.frames), o = 0;
    for (var t = 0; t < m.frames; t++) {
        var c = m.counts[t], f = new Array(c);
        for (var i = 0; i < c; i++) { f[i] = { m: m.midi[o], s: m.sal[o] }; o++; }
        frames[t] = f;
    }
    return frames;
}

function track(audioBuffer, opts) {
    opts = opts || {};
    var slug = opts.slug, onProgress = opts.onProgress || null, signal = opts.signal || null;
    var t0 = performance.now();
    return abortable(preload(slug, {
        signal: signal,
        onProgress: function (p) { if (onProgress) onProgress(p * 0.3, 'model'); }
    }).then(function () {
        return to22kMono(audioBuffer);
    }).then(function (pcm) {
        if (signal && signal.aborted) throw new DOMException('aborted', 'AbortError');
        var id = ++seq;
        // Silence watchdog: the transform is one long step (≈ a tenth of the song), each
        // chunk after it a short one — the ceiling covers the transform of a long song.
        var quietMs = Math.max(180000, Math.round(audioBuffer.duration * 1000));
        return new Promise(function (resolve, reject) {
            var cancelWd = null;
            var arm = function () {
                if (cancelWd) cancelWd();
                cancelWd = visibleTimeout(function () {
                    if (!pending[id]) return;
                    delete pending[id];
                    tele(slug, 'choir_track_stalled', { quiet_ms: quietMs });
                    release();
                    reject(typed('track_stalled', 'no step for ' + quietMs + ' ms'));
                }, quietMs);
            };
            var end = function (fn) { return function (v) { if (cancelWd) cancelWd(); fn(v); }; };
            pending[id] = {
                resolve: end(resolve), reject: end(reject),
                onProgress: function (done, of) { arm(); if (onProgress) onProgress(0.3 + 0.7 * done / of, 'track'); }
            };
            arm();
            worker.postMessage({ type: 'track', id: id, pcm: pcm }, [pcm.buffer]);
        });
    }), signal).then(function (m) {
        var frames = unpack(m);
        tele(slug, 'choir_track_ok', { sec: Math.round(audioBuffer.duration), ms: Math.round(performance.now() - t0), work_ms: m.ms, ep: m.provider });
        return { frames: frames, hop: m.hop, provider: m.provider, ms: m.ms };
    }).catch(function (e) {
        if ((signal && signal.aborted) || (e && (e.name === 'AbortError' || e.code === 'cancelled' || e.code === 'released'))) throw new DOMException('aborted', 'AbortError');
        tele(slug, 'choir_track_fail', { code: e && e.code || null, msg: String(e && e.message || e).slice(0, 80) });
        if (e && e.code === 'gpu_output_corrupt') release();
        throw e;
    });
}

window.ChoirPitch = { support: support, preload: preload, track: track, release: release,
    isWarm: function () { return !!readyInfo; }, info: function () { return readyInfo; } };
})();
